import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { getOrdinalsStrayFunds, type IOrdinalsStrayFunds, recoverOrdinalsStrayFunds } from 'api/api'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { moveOrdinalsStrayFundsWithDevice } from 'lib/ordinals-stray-sweep-flow'
import {
    ordinalsStrayFundsErrorMessage,
    ordinalsStrayFundsPollInterval,
    ordinalsStrayFundsView,
    STRAY_FUNDS_MOVED_NOTICE_MS,
} from './ordinalsStrayFundsPolicy'
import styles from './ordinals-stray-funds.module.scss'

// Plain BSV an external wallet paid to the ordinals address is not part of the
// balance. This offers to move it into the payment address and reports the
// sweep; it renders nothing when there is nothing to move or report.
export default function OrdinalsStrayFunds({
    enabled = true,
    className,
}: {
    enabled?: boolean
    className?: string
}) {
    const queryClient = useQueryClient()
    const userId = gon.currentUser?.id
    const queryKey = ['ordinalsStrayFunds', userId]
    const { formatSatsAmount } = useBsvDisplay()
    const [startError, setStartError] = useState<string | null>(null)
    const [noticeNow, setNoticeNow] = useState(() => Date.now())

    const { data } = useQuery({
        queryKey,
        queryFn: getOrdinalsStrayFunds,
        enabled: enabled && !!userId,
        staleTime: 15 * 1000,
        retry: false,
        refetchInterval: query => ordinalsStrayFundsPollInterval(query.state.data),
    })

    const recover = useMutation({
        // Device-signed wallets sign the move here, while the user is at the button.
        mutationFn: () =>
            data?.signing === 'device'
                ? moveOrdinalsStrayFundsWithDevice()
                : recoverOrdinalsStrayFunds(),
        onMutate: () => setStartError(null),
        onSuccess: response => {
            queryClient.setQueryData<IOrdinalsStrayFunds>(queryKey, old =>
                old ? { ...old, recovery: response.recovery } : old,
            )
        },
        onError: (error: Error) => setStartError(error.message || 'Unable to move the funds'),
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    })

    const finishedAt = data?.recovery?.status === 'succeeded' ? data.recovery.finished_at : null
    useEffect(() => {
        if (!finishedAt) return
        const remainingMs = Date.parse(finishedAt) + STRAY_FUNDS_MOVED_NOTICE_MS - Date.now()
        if (!Number.isFinite(remainingMs) || remainingMs <= 0) return

        const timeout = window.setTimeout(() => setNoticeNow(Date.now()), remainingMs)
        return () => window.clearTimeout(timeout)
    }, [finishedAt])

    const view = ordinalsStrayFundsView(data, Math.max(noticeNow, Date.now()))
    if (view.kind === 'hidden') return null

    const amount = formatSatsAmount(view.satoshis, { compactSats: false })
    const rootClassName = className ? `${styles.strayFunds} ${className}` : styles.strayFunds

    if (view.kind === 'moving') {
        return (
            <div className={rootClassName} role="status" data-testid="ordinals-stray-funds">
                <i className={`fa fa-spinner fa-spin ${styles.icon}`} aria-hidden="true" />
                <div className={styles.body}>
                    <div className={styles.title}>
                        {view.satoshis > 0
                            ? `Moving ${amount} to your balance…`
                            : 'Moving funds to your balance…'}
                    </div>
                    <div>This usually takes under a minute.</div>
                </div>
            </div>
        )
    }

    if (view.kind === 'moved') {
        return (
            <div className={rootClassName} role="status" data-testid="ordinals-stray-funds">
                <i className={`fa fa-check ${styles.icon}`} aria-hidden="true" />
                <div className={styles.body}>
                    <div className={styles.title}>Moved {amount} to your balance</div>
                    <a
                        className={styles.link}
                        href={`https://whatsonchain.com/tx/${view.txid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View transaction
                    </a>
                </div>
            </div>
        )
    }

    const errorMessage = startError || ordinalsStrayFundsErrorMessage(view.errorCode)

    return (
        <div className={rootClassName} role="status" data-testid="ordinals-stray-funds">
            <div className={styles.body}>
                <div className={styles.title}>{amount} was sent to your ordinals address</div>
                <div>That address is for ordinals, so this BSV isn’t in your balance yet.</div>
                {errorMessage && <div className={styles.error}>{errorMessage}</div>}
            </div>
            <button
                type="button"
                className={styles.action}
                onClick={() => recover.mutate()}
                disabled={recover.isPending}
            >
                {recover.isPending
                    ? view.signsOnDevice
                        ? 'Signing…'
                        : 'Starting…'
                    : 'Move to balance'}
            </button>
        </div>
    )
}
